function WarningConnectModal({
  show,
  setShow,
  setShowMetaDialog,
  lang,
  instagramPages = [],
  facebookPages = [],
  linePages = [],
  tiktokPages = [],
  savePage,
}) {
  // Process expiry dates for pages
  const processPageExpiryDates = (pages) => {
    if (!pages || pages.length === 0) return;
    
    pages.forEach((page) => {
      if (
        page.businessDetail &&
        page.businessDetail.package_expiry &&
        !page.businessDetail.modifiedDate
      ) {
        const expiryDate = moment(page.businessDetail.package_expiry);
        const isExpired = expiryDate.isBefore(moment(), "day");
        page.businessDetail.package_expiry = expiryDate.format("D MMM YYYY");
        page.businessDetail.isExpired = isExpired;
        page.businessDetail.modifiedDate = true;
      }
    });
  };

  // Process all page types
  processPageExpiryDates(facebookPages);
  processPageExpiryDates(instagramPages);
  processPageExpiryDates(linePages);
  processPageExpiryDates(tiktokPages);

  const facebookPagesWarning = facebookPages.filter((page) => page.warning);
  const instagramPagesWarning = instagramPages.filter((page) => page.warning);

  // Compute total warnings
  const totalWarnings =
    facebookPagesWarning.length +
    instagramPagesWarning.length +
    (linePages?.length || 0) +
    (tiktokPages?.length || 0);

  const [isUpdating, setIsUpdating] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCancel = () => {
    if ((linePages && linePages.length > 0) || (tiktokPages && tiktokPages.length > 0)) {
      setShow(false);
    } else {
      setShow(false);
      if (typeof setShowMetaDialog === "function") {
        setShowMetaDialog(true);
      }
    }
  };

  const submitMetaSetting = () => {
    setIsUpdating(true);
    Promise.resolve(savePage(true)).then(() => {
      setIsUpdating(false);
      setShow(false);
    }).catch((error) => {
      setIsUpdating(false);
      console.error('Error saving page:', error);
    });
  };

  // Render a single page item
  const renderPageItem = (page, iconSrc, nameField = 'name') => {
    const isExpired = page.businessDetail.isExpired;
    
    return (
      <div
        key={`${iconSrc}-${page[nameField]}`}
        className="page-item"
      >
        <div className="page-icon">
          <img
            src={iconSrc}
            width={30}
            height={30}
            alt={page[nameField]}
          />
        </div>
        <div className="sp-col page-info">
          <div className="sp-text -ellipsis -align-middle page-name">
            {page[nameField]}
          </div>
          <div className="sp-text -ellipsis page-connected">
            {lang.__connected_with}: {page.businessDetail.name}
          </div>
        </div>
        <div className={`page-package ${isExpired ? '-expired' : ''}`}>
          <div className="package-badge-wrapper">
            <div className="package-badge">
              {page.businessDetail.package_name}
            </div>
          </div>
          <div className={`sp-text-help -ellipsis package-expiry ${isExpired ? '-expired' : ''}`}>
            {isExpired ? lang.__expired : lang.__expires}
            : {page.businessDetail.package_expiry}
          </div>
        </div>
      </div>
    );
  };

  // Determine which description to use based on account types present
  const getDescription = () => {
    const hasFacebook = facebookPagesWarning && facebookPagesWarning.length > 0;
    const hasInstagram = instagramPagesWarning && instagramPagesWarning.length > 0;
    const hasLine = linePages && linePages.length > 0;
    const hasTikTok = tiktokPages && tiktokPages.length > 0;
    
    // Line is always alone, never mixed with Facebook or Instagram
    if (hasLine) {
      return lang.__change_account_description_line;
    }

    if (hasTikTok) {
      return (
        lang.__change_account_description_tiktok ||
        "This TikTok account is currently connected to another Business. If you proceed, the system will disconnect the previous connection and connect this account to the current Business instead."
      );
    }
    
    // Facebook and Instagram can be together
    if (hasFacebook && hasInstagram) {
      return lang.__change_account_description_facebook_instagram;
    } else if (hasFacebook) {
      return lang.__change_account_description_facebook;
    } else if (hasInstagram) {
      return lang.__change_account_description_instagram;
    }
    
    // Fallback (shouldn't happen)
    return lang.__change_account_description_facebook_instagram;
  };

  return (
    <div className="warning-connect-modal">
      <div
        className={`modal ${show ? "fade in" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: show ? "block" : "none" }}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <div className="modal-header-icon">
                !
              </div>
              <div className="sp-label">{lang.__change_account_header}</div>
            </div>
            <div
              className="modal-body"
              style={{
                maxHeight: "80vh",
                overflow: "hidden",
                scrollBehavior: "smooth",
                padding: "unset",
              }}
            >
              <div className="sp-text -regular modal-body-description">
                {getDescription()}
              </div>
              <div className="body -scrollable sp-list modal-body-scrollable">
                {facebookPagesWarning.length > 0 &&
                  facebookPagesWarning.map((page) => 
                    renderPageItem(page, "../public/img/facebook.png", "name")
                  )}
                {instagramPagesWarning.length > 0 &&
                  instagramPagesWarning.map((page) => 
                    renderPageItem(page, "../public/img/instagram_icon.png", "username")
                  )}
                {linePages && linePages.length > 0 &&
                  linePages.map((page) => 
                    renderPageItem(page, "../public/img/LINE_SOCIAL_Basic.png", "name")
                  )}
                {tiktokPages && tiktokPages.length > 0 &&
                  tiktokPages.map((page) =>
                    renderPageItem(page, "../public/img/TikTok.png", "name")
                  )}
              </div>
            </div>
            <div className="modal-footer">
              <div className={`sp-text-help footer-warning ${isMobile ? '-mobile' : '-desktop'}`}>
                {lang.__change_account_warning_description}
              </div>
              <div className={`footer-actions ${isMobile ? '-mobile' : '-desktop'}`}>
                <div className={`sp-text selected-count ${isMobile ? '-mobile' : '-desktop'}`}>
                  {lang.__you_choose} {totalWarnings} {lang.__list}
                </div>
                <div className={`button-group ${isMobile ? '-mobile' : '-desktop'}`}>
                  <ButtonCancel
                    className={`btn-cancel ${isMobile ? '-mobile' : '-desktop'}`}
                    onClick={handleCancel}
                  >
                    {lang.__back}
                  </ButtonCancel>
                  <button
                    className={`sp-btn -blue btn-apply ${isMobile ? '-mobile' : '-desktop'}`}
                    onClick={submitMetaSetting}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <i className="icon icon-circle-notch animate-spin"></i>
                    ) : (
                      lang.__analytic_apply
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={`modal-backdrop ${show ? "fade in" : ""}`}
        style={{ display: show ? "" : "none" }}
      ></div>
    </div>
  );
}
